"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import { Checkbox } from "@/components/checkbox";
import { Tag } from "@/components/tag";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";

export type NestedOption = {
  value: string;
  label: string;
};

export type NestedOptionGroup = {
  label: string;
  options: NestedOption[];
};

type NestedMultiComboboxProps = {
  className?: string;
  containerClassName?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  groups: NestedOptionGroup[];
  /** Option values offered as one-tap pills under the field — the handful people pick most. */
  shortcuts?: string[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (values: string[]) => void;
  maxItems?: number;
  maxVisibleTags?: number;
  id?: string;
};

const DEFAULT_MAX_VISIBLE_TAGS = 6;
const UNGROUPED_LABEL = "Other";

const matches = (text: string, query: string) => text.toLowerCase().includes(query.toLowerCase());

export const NestedMultiCombobox: FC<NestedMultiComboboxProps> = ({
  className,
  containerClassName,
  label,
  message,
  state = "default",
  required,
  disabled,
  placeholder = "Please select",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  groups,
  shortcuts,
  value,
  defaultValue = [],
  onValueChange,
  maxItems,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string[]>(defaultValue);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selected = value ?? internal;
  const atCap = maxItems !== undefined && selected.length >= maxItems;

  const namedGroups = useMemo(
    () => groups.map((group) => ({ ...group, label: group.label || UNGROUPED_LABEL })),
    [groups],
  );

  const allOptions = useMemo(() => groups.flatMap((group) => group.options), [groups]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return namedGroups;

    return namedGroups
      .map((group) =>
        matches(group.label, search)
          ? group
          : { ...group, options: group.options.filter((option) => matches(option.label, search)) },
      )
      .filter((group) => group.options.length > 0);
  }, [namedGroups, search]);

  const navigable = useMemo(() => filteredGroups.flatMap((group) => group.options), [filteredGroups]);
  const navigableIndex = useMemo(
    () => new Map(navigable.map((option, index) => [option.value, index])),
    [navigable],
  );

  const optionsByValue = useMemo(
    () => new Map(allOptions.map((option) => [option.value, option])),
    [allOptions],
  );

  const shortcutOptions = (shortcuts ?? []).flatMap((shortcut) => {
    const option = optionsByValue.get(shortcut);
    return option ? [option] : [];
  });

  const selectedOptions = allOptions.filter((option) => selected.includes(option.value));
  const visible = maxVisibleTags > 0 ? selectedOptions.slice(0, maxVisibleTags) : selectedOptions;
  const hidden = selectedOptions.length - visible.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      setHighlightedIndex(0);
    }
  };

  const toggle = (optionValue: string) => {
    const isSelected = selected.includes(optionValue);
    if (!isSelected && atCap) return;

    const next = isSelected ? selected.filter((v) => v !== optionValue) : [...selected, optionValue];
    setInternal(next);
    onValueChange?.(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, navigable.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const option = navigable[highlightedIndex];
        if (option) {
          event.preventDefault();
          toggle(option.value);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-8", containerClassName)}>
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root
        /* Without this the wheel scrolls whatever the field sits inside (a drawer, a page) instead
           of the option list — `modal` locks scrolling everywhere outside the popover. See
           phone-input.tsx's `CountrySelect` for the same fix. */
        modal
        open={open}
        onOpenChange={handleOpenChange}
      >
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          className={cn(
            "flex min-h-62 w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue",
            "disabled:cursor-not-allowed disabled:bg-neutral-black/5",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            className,
          )}
        >
          {selected.length === 0 ? (
            <span className="text-16 text-neutral-black/35">{placeholder}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-8">
              {visible.map((option) => (
                <Tag
                  key={option.value}
                  size="s"
                  className="max-w-full"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(option.value);
                  }}
                >
                  <span className="truncate">{option.label}</span>
                </Tag>
              ))}
              {hidden > 0 && (
                <Tag size="s" iconRight={false} className="text-neutral-black/70">
                  +{hidden}
                </Tag>
              )}
            </span>
          )}
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement).querySelector("input")?.focus();
            }}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex items-center gap-8 rounded-small border border-neutral-mid-grey bg-neutral-white px-12 py-8">
              <MagnifyingGlassIcon className="size-20 shrink-0 text-neutral-black/35" aria-hidden />
              <input
                autoFocus
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
              />
            </div>

            {navigable.length === 0 ? (
              <p className="px-8 py-12 text-14 text-neutral-grey">{emptyText}</p>
            ) : (
              <div className="flex max-h-280 flex-col gap-4 overflow-y-auto">
                {filteredGroups.map((group) => (
                  <div key={group.label || "ungrouped"} className="flex flex-col gap-4">
                    {group.label && (
                      <p className="px-8 pt-8 text-14 font-semibold text-neutral-grey">{group.label}</p>
                    )}
                    {group.options.map((option) => {
                      const index = navigableIndex.get(option.value) ?? 0;
                      const isSelected = selected.includes(option.value);
                      const disabled = !isSelected && atCap;

                      return (
                        <label
                          key={option.value}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={cn(
                            "flex items-center gap-8 rounded-small py-8 pr-8 transition-colors duration-100",
                            group.label ? "pl-24" : "pl-8",
                            disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                            index === highlightedIndex && "bg-tone-aqua-tint",
                          )}
                        >
                          <Checkbox
                            size="m"
                            checked={isSelected}
                            disabled={disabled}
                            onCheckedChange={() => toggle(option.value)}
                          />
                          <span className="min-w-0 text-16 text-neutral-black">{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {maxItems !== undefined && (
              <p className="px-8 pt-8 text-14 text-neutral-grey">
                {selected.length}/{maxItems} selected
              </p>
            )}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      {shortcutOptions.length > 0 && (
        <div className="flex flex-wrap gap-8">
          {shortcutOptions.map((option) => {
            const isSelected = selected.includes(option.value);
            const disabled = !isSelected && atCap;

            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isSelected}
                disabled={disabled}
                onClick={() => toggle(option.value)}
                className={cn(
                  "rounded-[1000px] border px-12 py-4 text-14 transition-colors duration-100",
                  isSelected
                    ? "border-brand-blue bg-tone-aqua-tint text-neutral-black"
                    : "border-neutral-mid-grey bg-neutral-white text-neutral-black hover:bg-tone-aqua-tint",
                  disabled && "cursor-not-allowed opacity-40 hover:bg-neutral-white",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
